/**
 * ---------------------------------------------------------
 * EntitySearchSelect
 * ---------------------------------------------------------
 *
 * Generic reusable dropdown with:
 * - search input
 * - debounced query
 * - infinite scroll (TanStack Query)
 * - selected entity sync via detail hook
 *
 * Designed for entity-based selection (customer, project, user, service, etc).
 *
 * 📌 Architecture:
 * - useInfiniteHook → must return Paginated<T> with { items }
 * - useDetailHook → must return single entity by id
 * - getLabel → defines how entity is shown inside input
 *
 * 📌 When to use:
 * - Selecting one entity from server-side paginated list
 * - Need search + infinite scroll
 * - Need selected id synchronization
 *
 * 📌 Not for:
 * - static options
 * - multi-select
 * - small local arrays
 *
 * ---------------------------------------------------------
 *
 * Example (Customer):
 *
 * <EntitySearchSelect
 *   value={customerId}
 *   onChange={(id) => setCustomerId(id)}
 *   useInfiniteHook={useInfiniteCustomers}
 *   useDetailHook={useCustomerDetail}
 *   searchParamName="name"
 *   getLabel={(c) => c.name}
 * />
 *
 * ---------------------------------------------------------
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react'
import styles from './EntitySearchSelect.module.css'
import { useDebounce } from '../../../hooks/useDebounce'
import { useOutsideClick } from '../../../hooks/useOutsideClick'
import { Input } from '../../controls/Input/Input'
import { useFieldId, useFieldRequired } from '../../controls/FieldWrapper/FieldWrapper'

interface BaseEntity {
  id: number
}

interface Props<T extends BaseEntity> {

  /** Currently selected entity id */
  value: number | null | undefined

  /** Callback when entity selected */
  onChange: (id: number | null, item?: T) => void


  /** Infinite query hook returning Paginated<T> */
  useInfiniteHook: (
    params: any,
    options?: { enabled?: boolean }
  ) => any


  /** Detail query hook returning T by id */
  useDetailHook: (id: number | undefined,
    options?: { enabled?: boolean }
  ) => any
  /** Backend search parameter name (e.g. "name", "title") */
  searchParamName: string

  /** Page size for infinite loading */
  limit?: number

  /** Function defining label rendering inside input */
  getLabel: (item: T) => string

  /** Custom render for dropdown item */
  renderItem?: (item: T) => React.ReactNode

  placeholder?: string
  disabled?: boolean
  readOnly?: boolean
  className?: string

  /**
   * The validation message, shown under the input.
   *
   * Replaces the `hasError` boolean this used to take. A boolean could only ever produce one
   * sentence — "Eingabefehler. Bitte überprüfen Sie die Daten." — so the caller's real message was
   * thrown away at the prop boundary: an empty Handwerker on the Termin form said that instead of
   * `Pflichtfeld`, which is what its schema actually returns.
   */
  error?: string | null

  /**
   * Forwarded to the search box.
   *
   * Exists so a `Controller` can hand this its `field.ref`: react-hook-form's `shouldFocusError`
   * (on by default) focuses the first invalid field, but only one that registered a ref. Without
   * it, submitting a Termin with no Handwerker moved focus nowhere and the error sat unnoticed
   * further up a scrolled form.
   */
  inputRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>

  extraParams?: Record<string, unknown>
  extraValues?: T[]

  onInputChange?: (value: string) => void
  inputValue?: string

}


export function EntitySearchSelect<T extends BaseEntity>({ // TODO3 documentation 
  value,
  onChange,
  useInfiniteHook,
  useDetailHook,
  searchParamName,
  limit = 20,
  getLabel,
  renderItem,
  placeholder = 'Suchen…',
  disabled,
  readOnly,
  className,
  extraParams,
  error,
  inputRef,
  extraValues,
  onInputChange,
  inputValue
}: Props<T>) {
  const [internalQuery, setInternalQuery] = useState('')
  const query = inputValue !== undefined
    ? inputValue
    : internalQuery

  const [isOpen, setIsOpen] = useState(false)
  /** Where the arrow keys are in the result list. `-1` while nothing is highlighted. */
  const [activeIndex, setActiveIndex] = useState(-1)

  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const activeItemRef = useRef<HTMLDivElement>(null)
  const listboxId = useId()
  const fieldId = useFieldId()
  const isRequired = useFieldRequired()

  const debouncedQuery = useDebounce(query, 400)
  const filters = useMemo(() => {
    const base = {
      limit,
      ...extraParams,
    }

    if (debouncedQuery?.trim()) {
      return {
        ...base,
        [searchParamName]: debouncedQuery.trim(),
      }
    }

    return base
  }, [limit, searchParamName, debouncedQuery, extraParams])




  const [hasOpened, setHasOpened] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setHasOpened(true)
    }
  }, [isOpen])

  // 🔹 detail only if value exists
  const { data: selectedItem } = useDetailHook(value ?? undefined, {
    enabled: !!value,
  })

  // 🔹 list only after first open
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteHook(filters, {
    enabled: isOpen,
  })

  const apiItems: T[] = data?.items ?? []

  const items: T[] = useMemo(() => {
    if (!extraValues?.length) return apiItems

    return [...extraValues, ...apiItems]
  }, [apiItems, extraValues])






  /**
   * Sync the visible label to the selected entity.
   *
   * `!query` alone was not enough: it only fires while the box is still empty, so a `value` that
   * changes from **outside** — a form field written by something other than this select, e.g. the
   * disposition board reassigning the Handwerker when a block is dragged into another row — left the
   * previous name on screen while the form already held the new id.
   *
   * Keyed on the value it last wrote, so typing a search term is still never clobbered: `value` does
   * not change while the user types, and the label is only rewritten when it actually moved on.
   */
  const syncedValueRef = useRef<number | null | undefined>(undefined)

  useEffect(() => {
    // The detail query keeps the previous entity around while it refetches; labelling with it would
    // put the old name over a selection the user has just made.
    if (!selectedItem || selectedItem.id !== value) return
    if (query && syncedValueRef.current === value) return

    setInternalQuery(getLabel(selectedItem))
    syncedValueRef.current = value
  }, [selectedItem, value])

  useOutsideClick(containerRef, () => setIsOpen(false), isOpen)

  const handleScroll = () => {
    const el = listRef.current
    if (!el || !hasNextPage || isFetchingNextPage) return

    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      fetchNextPage()
    }
  }

  const handleSelect = (item: T) => {
    onChange(item.id, item)

    const label = getLabel(item)

    if (inputValue === undefined) {
      setInternalQuery(label)
    }

    onInputChange?.(label)   // 🔥 ОСЬ ЦЕ ГОЛОВНЕ

    setIsOpen(false)
    setActiveIndex(-1)
  }

  /**
   * Keyboard operation of the result list, which this control had none of.
   *
   * **Enter must be swallowed here.** The search box is an `<input type="search">` — a submitting
   * control — and the forms it sits in now submit on Enter (see `Modal`'s
   * `SubmitButtonConfig.formId`). Left alone, pressing Enter to pick a Handwerker would instead
   * save the Termin with no Handwerker set. `preventDefault` while the list is open is what keeps
   * the two apart; with the list closed Enter falls through and submits, which is what a user who
   * has finished with this field expects.
   *
   * Escape closes the list and stops there — `modalStack` listens on the bubble phase, so a second
   * press reaches the dialog.
   */
  // `Input` renders either an `<input>` or a `<textarea>`, so its `onKeyDown` is typed for both.
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (disabled || readOnly) return

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
          setActiveIndex(0)
          return
        }
        if (items.length === 0) return
        const step = event.key === 'ArrowDown' ? 1 : -1
        setActiveIndex((prev) => {
          const next = prev + step
          if (next < 0) return items.length - 1
          if (next >= items.length) return 0
          return next
        })
        return
      }
      case 'Enter': {
        if (!isOpen) return
        const item = items[activeIndex]
        // An open list with nothing highlighted still swallows Enter rather than submitting
        // underneath a dropdown the user is looking at.
        event.preventDefault()
        if (item) handleSelect(item)
        return
      }
      case 'Escape': {
        if (!isOpen) return
        event.preventDefault()
        event.stopPropagation()
        setIsOpen(false)
        setActiveIndex(-1)
        return
      }
      case 'Tab': {
        if (isOpen) {
          setIsOpen(false)
          setActiveIndex(-1)
        }
      }
    }
  }

  // A new result set invalidates the old position — the highlight would otherwise point at whatever
  // now happens to sit at that index.
  useEffect(() => {
    setActiveIndex(-1)
  }, [debouncedQuery])

  useEffect(() => {
    if (isOpen) activeItemRef.current?.scrollIntoView({ block: 'nearest' })
  }, [isOpen, activeIndex])

  const isEmptySearch =
    !isFetching &&
    items.length === 0 &&
    debouncedQuery?.trim().length > 0

  return (
    <div ref={containerRef} className={styles.main}>
      <Input
        ref={inputRef}
        // Set by the enclosing `FieldWrapper`, whose `<label>` points here.
        id={fieldId}
        aria-required={isRequired || undefined}
        value={query}
        onChange={(value) => {
          if (readOnly) return
          setInternalQuery(value)
          onInputChange?.(value)
          if (value.trim() === '') {
            onChange(null)
          }
          if (!isOpen) setIsOpen(true)
        }}
        onFocus={() => { if (!readOnly) setIsOpen(true) }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        type="search"
        error={error}
        className='w-full'
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          isOpen && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
        }
      />

      {isOpen && (
        <div
          ref={listRef}
          id={listboxId}
          role="listbox"
          className={styles.list}
          onScroll={handleScroll}
        >
          {items.map((item, index) => {
            const isActive = index === activeIndex
            return (
              <div
                key={item.id}
                id={`${listboxId}-option-${index}`}
                ref={isActive ? activeItemRef : null}
                role="option"
                aria-selected={item.id === value}
                className={`${styles.item} ${isActive ? styles.itemActive : ''}`}
                onClick={() => handleSelect(item)}
                // One highlight for both inputs, so hovering does not leave a stale Enter target.
                onMouseEnter={() => setActiveIndex(index)}
              >
                {renderItem
                  ? renderItem(item)
                  : getLabel(item)}
              </div>
            )
          })}

          {isFetchingNextPage && (
            <div className={styles.muted}>Loading…</div>
          )}

          {isEmptySearch && (
            <div className={styles.muted}>
              Keine Ergebnisse
            </div>
          )}
        </div>
      )}
    </div>
  )
}
